import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Clock } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { PublicEventType, SchedulingHost } from '../../types'

export default function Profile({ host, eventTypes, embed }: { host: SchedulingHost, eventTypes: PublicEventType[], embed: boolean }) {
  const { t } = useTranslation()
  const suffix = React.useMemo(() => {
    if (!embed) return ''
    const current = new URLSearchParams(window.location.search)
    const next = new URLSearchParams({ embed: '1' })
    ;['closable', 'theme'].forEach(key => { const value = current.get(key); if (value) next.set(key, value) })
    return `?${next.toString()}`
  }, [embed])

  return (
    <div className='mx-auto flex w-full max-w-2xl flex-col gap-6'>
      <div className='flex flex-col items-center gap-2 text-center'>
        <Avatar className='size-16'>
          {host.avatar_url && <AvatarImage src={host.avatar_url} alt={host.name} />}
          <AvatarFallback>{host.name?.[0]}</AvatarFallback>
        </Avatar>
        <h1 className='text-xl font-semibold'>{host.name}</h1>
      </div>
      {eventTypes.length === 0 ? (
        <p className='text-center text-muted-foreground'>{t('Scheduling::No event types available.')}</p>
      ) : (
        <ul className='divide-y overflow-hidden rounded-xl border bg-background'>
          {eventTypes.map(eventType => (
            <li key={eventType.id}>
              <a href={`/${eventType.booking_slug}/${eventType.slug}${suffix}`} className='flex min-h-16 items-center justify-between gap-4 p-4 hover:bg-muted'>
                <div className='flex flex-col gap-1'>
                  <span className='font-medium'>{eventType.title}</span>
                  <div className='flex flex-wrap gap-1'>
                    {eventType.durations.map(d => (
                      <Badge key={d} variant='secondary'><Clock className='size-3' />{t('Scheduling::{{count}}m', { count: d })}</Badge>
                    ))}
                  </div>
                </div>
                <ArrowRight className='size-4 shrink-0 text-muted-foreground' />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
